<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="goBack"/>
        
        <div class="page-header">
            <h2 class="page-title">📊 {{ kalaName }}</h2>
            <div class="header-actions">
                <button class="refresh-btn" @click="fetchData" :disabled="loading">
                    <span class="refresh-icon" :class="{ spinning: loading }">⟳</span>
                    {{ loading ? 'در حال بارگیری...' : 'بروزرسانی' }}
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>در حال بارگیری اطلاعات...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <p>{{ error }}</p>
            <button @click="fetchData" class="retry-btn">تلاش مجدد</button>
        </div>

        <!-- Data Display -->
        <div v-else-if="depotData.length > 0" class="depot-content">
            <!-- Summary Cards -->
            <div class="depot-summary">
                <div class="summary-item">
                    <span class="summary-label">موجودی کل</span>
                    <span class="summary-value">{{ formatNumber(depotData[0]?.sumX || 0) }}</span>
                </div>
                <div class="summary-item">
                    <span class="summary-label">واحد</span>
                    <span class="summary-value">{{ depotData[0]?.Vahed || '-' }}</span>
                </div>
                <div class="summary-item">
                    <span class="summary-label">تعداد تراکنش‌ها</span>
                    <span class="summary-value">{{ depotData.length }}</span>
                </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-container desktop-view">
                <table class="depot-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>تاریخ</th>
                            <th>حساب</th>
                            <th>نام Act</th>
                            <th>نوع</th>
                            <th>ورودی</th>
                            <th>خروجی</th>
                            <th>مانده</th>
                            <th>مبلغ</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in depotData" :key="item.IDD || index" :class="getTransactionType(item)">
                            <td class="row-number">{{ index + 1 }}</td>
                            <td class="date-cell">{{ formatDate(item.Dates) }}</td>
                            <td class="account-name-cell">{{ item.TitleTaf1 || item.NameAccount || '-' }}</td>
                            <td class="act-name-cell">{{ item.NameAct || '-' }}</td>
                            <td>
                                <span class="type-badge" :class="getTransactionType(item)">
                                    {{ getTransactionTypeText(item) }}
                                </span>
                            </td>
                            <td class="amount-cell positive">
                                {{ formatNumber(item.V || 0) }}
                                <span class="unit-text" v-if="item.Vahed">{{ item.Vahed }}</span>
                            </td>
                            <td class="amount-cell negative">
                                {{ formatNumber(item.S || 0) }}
                                <span class="unit-text" v-if="item.Vahed">{{ item.Vahed }}</span>
                            </td>
                            <td class="amount-cell balance">
                                {{ formatCurrency(item.Mande_Rial || 0) }}
                            </td>
                            <td class="amount-cell total">
                                {{ formatCurrency(item.Money_End || 0) }}
                            </td>
                            <td>
                                <button 
                                    class="view-details-btn"
                                    @click.stop="openCommercialDetails(item)"
                                >
                                    <span class="btn-icon">👁️</span>
                                    جزئیات
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mobile Card View -->
            <div class="mobile-view">
                <div v-for="(item, index) in depotData" :key="item.IDD || index" class="depot-card" :class="getTransactionType(item)">
                    <div class="card-header-top">
                        <span class="card-index">#{{ index + 1 }}</span>
                        <span class="card-value act-name">{{ item.NameAct || '-' }}</span>
                        <span class="type-badge" :class="getTransactionType(item)">
                            {{ getTransactionTypeText(item) }}
                        </span>
                    </div>
                    <div class="card-two-columns">
                        <!-- Right Column - Details -->
                        <div class="card-right">
                            <div class="card-row">
                                <span class="card-label">تاریخ</span>
                                <span class="card-value">{{ formatDate(item.Dates) }}</span>
                                <span class="card-divider-dot">•</span>
                                <span class="card-value act-name">{{ item.Invoice_Auto || '-' }}</span>
                            </div>
                            <div class="card-row">
                                <span class="card-label">حساب</span>
                                <span class="card-value account-name">{{ item.TitleTaf1 || item.NameAccount || '-' }}</span>
                            </div>
                            <div class="card-row">
                                <span class="card-label">مقدار</span>
                                <span class="card-value amount-value">
                                    {{ getQuantity(item) }} 
                                    <span class="unit-text">{{ item.Vahed || '' }}</span>
                                </span>
                                <span class="card-divider-dot">•</span>
                                <span class="card-label">فی</span>
                                <span class="card-value price-value">{{ formatCurrency(getFeValue(item)) }}</span>
                            </div>
                        </div>
                        
                        <!-- Divider Line -->
                        <div class="card-divider"></div>
                        
                        <!-- Left Column - Amount -->
                        <div class="card-left">
                            <div class="card-amount-label">مقدار مانده</div>
                            <div class="card-amount-value">{{ formatCurrency(item.sumX || 0) }}</div>
                        </div>
                    </div>
                    <div class="kala-actions">
                        <button 
                            class="view-details-btn"
                            @click.stop="openCommercialDetails(item)"
                        >
                            <span class="btn-icon">👁️</span>
                            جزئیات
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && !error" class="empty-state">
            <div class="empty-icon">📭</div>
            <h3>داده‌ای وجود ندارد</h3>
            <p>هیچ تراکنشی برای این کالا یافت نشد</p>
        </div>

        <!-- Commercial Details Modal -->
        <CommercialDetailsModal
            :visible="showCommercialModal"
            :invoice-id="selectedInvoiceId"
            :ac="selectedAc"
            :act-code="selectedActCode"
            @update:visible="showCommercialModal = $event"
            @print="printCommercialDetails"
            @close="closeCommercialModal"
        />
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import { SalesService } from '@/services/api'
import CommercialDetailsModal from '@/components/CommercialDetailsModal.vue'

export default {
    name: 'DepotCardDetail',
    components: {
        Navbar,
        CommercialDetailsModal
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        
        // State
        const depotData = ref([])
        const kalaName = ref('')
        const commodityCode = ref('')
        const loading = ref(false)
        const error = ref(null)
        
        // Commercial Modal State
        const showCommercialModal = ref(false)
        const selectedInvoiceId = ref(null)
        const selectedAc = ref(null)
        const selectedActCode = ref(null)
        
        // Methods
        const goBack = () => {
            router.push('/employer/kala-search')
        }
        
        const fetchData = async () => {
            if (!commodityCode.value) {
                error.value = 'کد کالا مشخص نشده است'
                return
            }
            
            loading.value = true
            error.value = null
            
            try {
                const response = await SalesService.getDepotCardPrice(commodityCode.value)
                
                if (response.data.success) {
                    depotData.value = response.data.data || []
                } else {
                    error.value = response.data.message || 'خطا در دریافت اطلاعات'
                }
            } catch (err) {
                console.error('Error fetching depot data:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
            } finally {
                loading.value = false
            }
        }
        
        const openCommercialDetails = (item) => {
            // Get the invoice number from the item
            const invoiceId = item.Invoice_Auto || item.InvoiceAuto
            if (!invoiceId) {
                console.warn('No invoice ID found for this item')
                return
            }
            
            // You may need to get ac and act from the item or use defaults
            // Adjust these based on your data structure
            selectedInvoiceId.value = invoiceId
            selectedAc.value = 2 // Default or get from item
            selectedActCode.value = item.Act || 279
            
            showCommercialModal.value = true
        }
        
        const closeCommercialModal = () => {
            showCommercialModal.value = false
            selectedInvoiceId.value = null
            selectedAc.value = null
            selectedActCode.value = null
        }
        
        const printCommercialDetails = () => {
            console.log('Printing commercial details...')
            // Implement print functionality
            //window.print()
        }
        
        const getTransactionType = (item) => {
            if (item.V > 0 && item.S === 0) return 'entry'
            if (item.S > 0 && item.V === 0) return 'exit'
            if (item.V > 0 && item.S > 0) return 'both'
            return 'unknown'
        }
        
        const getTransactionTypeText = (item) => {
            const types = {
                entry: 'وارده',
                exit: 'صادره',
                both: 'وارده/صادره',
                unknown: '-'
            }
            return types[getTransactionType(item)] || '-'
        }
        
        const getQuantity = (item) => {
            if (item.V > 0 && item.S === 0) {
                return formatNumber(item.V)
            } else if (item.S > 0 && item.V === 0) {
                return formatNumber(item.S)
            } else if (item.V > 0 && item.S > 0) {
                return formatNumber(item.V) + '/' + formatNumber(item.S)
            }
            return '۰'
        }
        
        const getFeValue = (item) => {
            if (item.V > 0) {
                return item.Fe_V || 0
            } else if (item.S > 0) {
                return item.Fe_S || 0
            }
            return 0
        }
        
        const formatDate = (dateStr) => {
            if (!dateStr) return '-'
            return dateStr
        }
        
        const formatNumber = (value) => {
            if (value === null || value === undefined) return '۰'
            return new Intl.NumberFormat('fa-IR').format(value)
        }
        
        const formatCurrency = (amount) => {
            if (amount === null || amount === undefined || amount === '') return '۰'
            let num = typeof amount === 'string' ? parseFloat(amount) : amount
            if (isNaN(num)) return '۰'
            num = Math.round(num * 100) / 100
            const formatted = new Intl.NumberFormat('fa-IR', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                useGrouping: true
            }).format(num)
            return formatted.replace(/٫/g, '.')
        }
        
        // Lifecycle
        onMounted(() => {
            const params = route.params
            commodityCode.value = params.id || route.query.commodity_code || ''
            kalaName.value = params.name || route.query.name || route.query.kalaName || 'کالا'
            
            if (commodityCode.value) {
                fetchData()
            } else {
                error.value = 'کد کالا مشخص نشده است'
            }
        })
        
        return {
            depotData,
            kalaName,
            commodityCode,
            loading,
            error,
            showCommercialModal,
            selectedInvoiceId,
            selectedAc,
            selectedActCode,
            goBack,
            fetchData,
            openCommercialDetails,
            closeCommercialModal,
            printCommercialDetails,
            getTransactionType,
            getTransactionTypeText,
            getQuantity,
            getFeValue,
            formatDate,
            formatNumber,
            formatCurrency
        }
    }
}
</script>

<style scoped>
.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
    direction: rtl;
    margin-bottom: 45px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px 24px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--border);
}

.page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--heading);
}

.header-actions {
    display: flex;
    gap: 10px;
    margin-bottom: 5px;
}

.refresh-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text);
}

.refresh-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.refresh-icon.spinning {
    animation: spin 1s linear infinite;
}

/* Copy all styles from your existing component */
.depot-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-item {
    background: var(--bg);
    padding: 16px;
    border-radius: 12px;
    text-align: center;
    border: 1px solid var(--border);
}

.summary-label {
    display: block;
    font-size: 12px;
    color: var(--subtext);
    margin-bottom: 4px;
}

.summary-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--text);
}

.table-container {
    overflow-x: auto;
    margin-bottom: 16px;
}

.depot-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    direction: rtl;
    min-width: 800px;
}

.depot-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--border);
}

.depot-table th {
    padding: 10px 12px;
    text-align: right;
    font-weight: 600;
    color: var(--subtext);
    white-space: nowrap;
    font-size: 12px;
}

.depot-table td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
    font-size: 13px;
}

.depot-table tbody tr:hover {
    background: var(--hover);
}

.amount-cell {
    font-variant-numeric: tabular-nums;
    direction: ltr;
    text-align: right;
}

.amount-cell.positive {
    color: var(--green);
}

.amount-cell.negative {
    color: var(--red);
}

.type-badge {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    display: inline-block;
}

.type-badge.entry {
    background: #ecfdf5;
    color: #059669;
}

.type-badge.exit {
    background: #fef2f2;
    color: #dc2626;
}

.type-badge.both {
    background: #fffbeb;
    color: #d97706;
}

.type-badge.unknown {
    background: var(--bg);
    color: var(--subtext);
}

.mobile-view {
    display: none;
    flex-direction: column;
    gap: 12px;
}

@media (max-width: 768px) {
    .desktop-view {
        display: none !important;
    }
    
    .mobile-view {
        display: flex !important;
    }
}

/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    overflow: hidden;
}

.depot-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 10px 12px 12px 12px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    transition: all 0.3s ease;
}

/* Color coding based on transaction type */
.depot-card.entry {
    border-right: 4px solid #22c55e;
    background: linear-gradient(to left, var(--card-bg), rgba(34, 197, 94, 0.05));
}

.depot-card.exit {
    border-right: 4px solid #ef4444;
    background: linear-gradient(to left, var(--card-bg), rgba(239, 68, 68, 0.05));
}

.depot-card.both {
    border-right: 4px solid #f59e0b;
    background: linear-gradient(to left, var(--card-bg), rgba(245, 158, 11, 0.05));
}

.depot-card.unknown {
    border-right: 4px solid var(--border);
}

/* Card Header Top */
.card-header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 6px;
    margin-bottom: 6px;
    border-bottom: 1px solid var(--border);
}

.card-index {
    font-size: 11px;
    font-weight: 600;
    color: var(--subtext);
}

.type-badge {
    padding: 2px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    display: inline-block;
    flex-shrink: 0;
}

.type-badge.entry {
    background: #22c55e;
    color: white;
}

.type-badge.exit {
    background: #ef4444;
    color: white;
}

.type-badge.both {
    background: #f59e0b;
    color: white;
}

.type-badge.unknown {
    background: var(--bg);
    color: var(--subtext);
}

/* Card Two Columns */
.card-two-columns {
    display: flex;
    align-items: stretch;
    gap: 0;
    min-height: 80px;
    width: 100%;
    box-sizing: border-box;
}

/* Right Column - Details */
.card-right {
    flex: 1;
    min-width: 0;
    padding: 4px 8px 4px 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    overflow: hidden;
    box-sizing: border-box;
}

.card-row {
    display: flex;
    align-items: center;
    padding: 1px 0;
    gap: 4px;
    width: 100%;
    box-sizing: border-box;
    flex-wrap: wrap;
}

.card-label {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 28px;
}

.card-value {
    font-size: 12px;
    color: var(--text);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
}

.card-value.act-name {
    color: var(--primary);
    font-weight: 500;
}

.card-value.account-name {
    font-weight: 500;
    color: var(--text);
    font-size: 11px;
}

.card-value.amount-value {
    font-weight: 700;
    color: var(--text);
    direction: ltr;
}

.card-value.price-value {
    color: var(--text);
    direction: ltr;
}

.card-divider-dot {
    color: var(--subtext);
    font-size: 10px;
    flex-shrink: 0;
    margin: 0 2px;
}

.unit-text {
    font-size: 10px;
    color: var(--subtext);
    font-weight: 400;
    margin-right: 2px;
}

/* Divider */
.card-divider {
    width: 2px;
    background: var(--border);
    flex-shrink: 0;
    margin: 4px 0;
}

/* Left Column - Amount */
.card-left {
    flex: 0 0 26%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    background: linear-gradient(135deg, var(--primary-light, #eef2ff), var(--bg));
    border-radius: 0 8px 8px 0;
    box-sizing: border-box;
    overflow: hidden;
}

/* Color variants for left column based on transaction type */
.depot-card.entry .card-left {
    background: linear-gradient(135deg, #dcfce7, #ecfdf5);
}

.depot-card.exit .card-left {
    background: linear-gradient(135deg, #fee2e2, #fef2f2);
}

.depot-card.both .card-left {
    background: linear-gradient(135deg, #fef3c7, #fffbeb);
}

.card-amount-label {
    font-size: 10px;
    color: var(--subtext);
    font-weight: 500;
    margin-bottom: 3px;
    text-align: center;
    white-space: nowrap;
}

.card-amount-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--primary);
    word-break: break-all;
    text-align: center;
    direction: ltr;
    max-width: 100%;
    overflow-wrap: break-word;
    line-height: 1.2;
}

/* Responsive */
@media (max-width: 768px) {
    .desktop-view {
        display: none;
    }
    
    .mobile-view {
        display: flex;
    }
    
    .card-left {
        flex: 0 0 24%;
        padding: 6px 3px;
    }
    
    .card-amount-value {
        font-size: 14px;
    }
    
    .card-right {
        padding: 3px 6px 3px 3px;
    }
}

@media (max-width: 480px) {
    .depot-card {
        padding: 8px 10px 10px 10px;
    }
    
    .card-two-columns {
        min-height: 70px;
    }
    
    .card-left {
        flex: 0 0 22%;
        padding: 4px 2px;
    }
    
    .card-amount-label {
        font-size: 9px;
    }
    
    .card-amount-value {
        font-size: 12px;
    }
    
    .card-right {
        padding: 2px 4px 2px 2px;
        gap: 2px;
    }
    
    .card-label {
        font-size: 9px;
        min-width: 20px;
    }
    
    .card-value {
        font-size: 10px;
    }
    
    .card-value.account-name {
        font-size: 10px;
    }
    
    .card-divider-dot {
        font-size: 8px;
        margin: 0 1px;
    }
    
    .type-badge {
        font-size: 9px;
        padding: 1px 8px;
    }
    
    .unit-text {
        font-size: 9px;
    }
    
    .card-divider {
        margin: 3px 0;
    }
    
    .card-header-top {
        padding-bottom: 4px;
        margin-bottom: 4px;
    }
    
    .card-index {
        font-size: 10px;
    }
}

/* Dark mode support */
[data-theme="dark"] .depot-card {
    background: #1a1a2e;
    border-color: #2d2d44;
}

[data-theme="dark"] .depot-card.entry {
    border-right-color: #22c55e;
    background: linear-gradient(to left, #1a1a2e, rgba(34, 197, 94, 0.08));
}

[data-theme="dark"] .depot-card.exit {
    border-right-color: #ef4444;
    background: linear-gradient(to left, #1a1a2e, rgba(239, 68, 68, 0.08));
}

[data-theme="dark"] .depot-card.both {
    border-right-color: #f59e0b;
    background: linear-gradient(to left, #1a1a2e, rgba(245, 158, 11, 0.08));
}

[data-theme="dark"] .depot-card.entry .card-left {
    background: linear-gradient(135deg, #064e3b, #1a1a2e);
}

[data-theme="dark"] .depot-card.exit .card-left {
    background: linear-gradient(135deg, #7f1d1d, #1a1a2e);
}

[data-theme="dark"] .depot-card.both .card-left {
    background: linear-gradient(135deg, #78350f, #1a1a2e);
}

[data-theme="dark"] .card-left {
    background: linear-gradient(135deg, #1a1a3e, #1a1a2e);
}

[data-theme="dark"] .type-badge.entry {
    background: #22c55e;
    color: white;
}

[data-theme="dark"] .type-badge.exit {
    background: #ef4444;
    color: white;
}

[data-theme="dark"] .type-badge.both {
    background: #f59e0b;
    color: white;
}
/* Desktop Table View - Styled like mobile cards */
.table-container.desktop-view {
    overflow-x: auto;
    margin: 16px 0;
    border-radius: 12px;
    border: 1px solid var(--card-border);
    background: var(--card-bg);
    box-shadow: 0 2px 8px var(--shadow-1);
}

.depot-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
    min-width: 800px;
}

.depot-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--card-border);
}

.depot-table thead th {
    padding: 12px 14px;
    text-align: center;
    font-weight: 600;
    color: var(--heading);
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid var(--card-border);
    white-space: nowrap;
}

.depot-table tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid var(--card-border);
}

.depot-table tbody tr:hover {
    background: var(--ring);
}

.depot-table tbody tr:last-child {
    border-bottom: none;
}

.depot-table tbody td {
    padding: 10px 14px;
    text-align: center;
    vertical-align: middle;
    color: var(--text);
    font-size: 13px;
}

/* Row Number */
.depot-table .row-number {
    font-weight: 600;
    color: var(--subtext);
    font-size: 12px;
    width: 40px;
}

/* Date Cell */
.depot-table .date-cell {
    font-weight: 500;
    color: var(--heading);
    font-size: 13px;
    white-space: nowrap;
}

/* Account Name */
.depot-table .account-name-cell {
    font-weight: 500;
    color: var(--heading);
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Act Name */
.depot-table .act-name-cell {
    color: var(--text);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Type Badge */
.depot-table .type-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    min-width: 50px;
}

.type-badge.income {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.type-badge.expense {
    background: rgba(249, 115, 22, 0.15);
    color: #ea580c;
}

.type-badge.transfer {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
}

.type-badge.adjustment {
    background: rgba(139, 92, 246, 0.15);
    color: #7c3aed;
}

/* Amount Cells */
.depot-table .amount-cell {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
}

.depot-table .amount-cell.positive {
    color: #059669;
}

.depot-table .amount-cell.negative {
    color: #ea580c;
}

.depot-table .amount-cell.balance {
    color: var(--heading);
    font-weight: 700;
}

.depot-table .amount-cell.total {
    color: var(--primary);
    font-weight: 700;
}

/* Unit Text */
.depot-table .unit-text {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 400;
    margin-right: 2px;
}

/* Row hover effect - like mobile cards */
.depot-table tbody tr {
    border-bottom: 1px solid var(--card-border);
}

.depot-table tbody tr:last-child {
    border-bottom: none;
}

/* Color coding rows by transaction type */
.depot-table tbody tr.income {
    border-right: 3px solid #059669;
}

.depot-table tbody tr.expense {
    border-right: 3px solid #ea580c;
}

.depot-table tbody tr.transfer {
    border-right: 3px solid #2563eb;
}

.depot-table tbody tr.adjustment {
    border-right: 3px solid #7c3aed;
}

/* Responsive - Hide table on mobile, show cards */
@media (max-width: 768px) {
    .desktop-view {
        display: none !important;
    }
    
    .mobile-view {
        display: block !important;
    }
}

@media (min-width: 769px) {
    .mobile-view {
        display: none !important;
    }
    
    .desktop-view {
        display: block !important;
    }
}

/* Optional: Scrollbar styling */
.table-container.desktop-view::-webkit-scrollbar {
    height: 6px;
}

.table-container.desktop-view::-webkit-scrollbar-track {
    background: var(--bg);
    border-radius: 4px;
}

.table-container.desktop-view::-webkit-scrollbar-thumb {
    background: var(--muted-2);
    border-radius: 4px;
}

.table-container.desktop-view::-webkit-scrollbar-thumb:hover {
    background: var(--muted);
}
.kala-actions {
    display: flex;
    gap: 10px;
}
.view-details-btn,
.add-to-cart-btn {
    padding: 5px 5px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
}

.view-details-btn {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
}

.view-details-btn:hover {
    background: var(--hover);
    transform: translateY(-2px);
}
</style>